import axios from 'axios';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { UNSUCCESSFULSELL, DEFAULT_CURRENCY } from '../constants';

/**
 * Get session ID from cookie
 * @returns {string} session ID
 */
export const getSessionIDFromCookie = (): string => {
  // Stub implementation for chat-components
  return '';
};

/**
 * This function will fetch mock modal from Local for hotel details page in local development mode.
 * @returns {object} mockmodal
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getMockPageModel = async (_page: string) => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await import('../mocks/hotelDetailsMock.js').then((data: any) => {
      return data?.HotelDetailsModel;
    });
    // : await import('./_localDevMocks/findHotelMock').then((data: any) => {
    //     return data?.FindHotelModel;
    //   });
    return response;
  } catch (err) {
    console.error(`Mock model loading failed: ${err}`);
    return null;
  }
};

/**
 * making an Page modal call on component load based on modelReqBody value
 * sets AEM modal on responsiveGridModelHQV variable based on AEM response
 * @param modelReqBody: Request body for hybrid config app in order to provide model
 * @param templateURL: hybrid config app URL from where we get AEM model
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getPageModel = async (modelReqBody: any, templateURL: string) => {
  let pageModel = {};
  try {
    const response = await axios.post(`${templateURL}`, modelReqBody);
    if (response) {
      pageModel = response?.data?.data;
    } else {
      pageModel = {};
    }
  } catch (err) {
    // log.error(`HQV_MODEL_CALL : API call failed: ${err}`);
    //returing page model and logging error
    return pageModel;
  }
  return pageModel;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getItemFromPageModel(pageModel: any, itemPath: string) {
  const parts = itemPath.split('/');
  let obj = pageModel;

  for (let i = 0; i < parts.length && obj; i++) {
    obj = (obj[':items'] || {})[parts[i]];
  }

  return obj || {};
}

// function to get the logger api url
export const getLoggerApiURL = (_NEXT_PUBLIC_PREFIX: string, isRemoteSpa = false) => {
  let loggerApiURL;
  if (isRemoteSpa) {
    loggerApiURL = '/api/shop-renderer/logger';
  } else {
    loggerApiURL = '/api/logger';
  }
  return loggerApiURL;
};

// Mock search result data loader for local dev
export const getMockSearchResultData = async () => {
  try {
    const response = await import('../organisms/OSearchResults/mock/mockSearchResultData').then(data => {
      return data?.SearchResultMockData;
    });
    return response;
  } catch (err) {
    console.error('Mock search result data loading failed:', err);
    return null;
  }
};

// Update session document error messages
export const updateSessionDocErrorMessages = async (
  _sessionID: string,
  _SESSION_APP_GET_CALL_URL: string,
  _sessionUpdateURL: string,
  _errorMessageKeys: string[]
) => {
  // Skip session document updates if SESSION_APP_GET_CALL_URL is not configured
  // This is expected for chat context where session document is not used
  if (!_SESSION_APP_GET_CALL_URL) {
    return {};
  }
  // Stub implementation for chat-components
  // In production, this would interact with session management
  return {};
};

// Get process environment variables (client or server)
export const getProcessEnvs = () => {
  const canUseDOM = typeof window !== 'undefined';
  if (canUseDOM) {
    const envTag = document.getElementById('__SERVERENV__');
    if (envTag && envTag.innerHTML) {
      const envObject = JSON.parse(envTag.innerHTML);
      return envObject;
    }
    return process.env;
  }
  return process.env;
};

// Capitalize first letter of text
export const capitalize = (text: string) => {
  return `${text?.charAt(0)?.toUpperCase()}${text?.slice(1)?.toLowerCase()}`;
};

/**
 * Converts Hong Kong, Macau, and Taiwan country codes to China (CN)
 * for analytics and search purposes.
 * @param countryCode - The country code to check
 * @returns 'CN' for HK, MO, TW; otherwise returns the original country code
 */
export const changeCountryCodeForChina = (countryCode: string) => {
  if (countryCode === 'HK' || countryCode === 'MO' || countryCode === 'TW') {
    return 'CN';
  } else {
    return countryCode;
  }
};

/**
 * Function to return the translated values of specified currency
 * @param currentLocale - Current locale string
 * @param currencyLabel - Currency label from static content
 * @param currency - Currency code (e.g., 'JPY', 'KRW')
 * @returns Translated currency label or currency code
 */
export const fetchCurrencyToLocale = (currentLocale: string, currencyLabel: string, currency?: string) => {
  const JP_LOCALE = 'ja-JP';
  const KR_LOCALE = 'ko-KR';

  switch (currentLocale) {
    case JP_LOCALE:
      return currency === 'JPY' ? currencyLabel : currency;
    case KR_LOCALE:
      return currency === 'KRW' ? currencyLabel : currency;
    default:
      return currency;
  }
};

/**
 * Handle property clicked - manages recently viewed properties in localStorage
 * @param recentlyViewed - Current recently viewed properties array
 * @param setRecentlyViewed - Function to update recently viewed state
 * @param id - Property ID to add to recently viewed
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const handlePropertyClicked = (recentlyViewed: any, setRecentlyViewed: any, id: string) => {
  const prevChecked = localStorage.getItem('recent')?.split(',');

  if (recentlyViewed && recentlyViewed.length > 0) {
    if (!recentlyViewed.includes(id)) {
      if (prevChecked && prevChecked.length > 0) {
        setRecentlyViewed([...prevChecked, ...recentlyViewed, id]);
      } else {
        setRecentlyViewed([...recentlyViewed, id]);
      }
    }
  } else {
    if (prevChecked && prevChecked.length > 0) {
      setRecentlyViewed([...prevChecked, id]);
    } else {
      setRecentlyViewed([id]);
    }
  }
};

/**
 * Safely construct an absolute/relative URL for redirects.
 * Accepts relative paths and absolute URLs
 * @param inputUrl - The URL to sanitize
 * @returns Sanitized URL or null if invalid
 */
export const sanitizeRedirectUrl = (inputUrl: string | null | undefined): string | null => {
  if (!inputUrl || typeof window === 'undefined') return null;
  try {
    const decoded = decodeURIComponent(inputUrl);
    const url = new URL(decoded);
    return url.href;
  } catch {
    return null;
  }
};

/**
 * Toggles the unsuccessfulSell URL parameter between 'true' and 'false'.
 * Used to track unsuccessful sell state in the URL for analytics and state management.
 */
export const toggleUnsuccessfulSellUrlParameter = (): void => {
  if (typeof window !== 'undefined') {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(window.location.search);
    if (params?.has(UNSUCCESSFULSELL)) {
      const currentParamValue = params?.get(UNSUCCESSFULSELL);
      const updatedParamsValue = currentParamValue === 'true' ? 'false' : 'true';
      params.set(UNSUCCESSFULSELL, updatedParamsValue);
      url.search = params.toString();
      window.history.replaceState({}, '', url);
    }
  }
};

/**
 * Conditionally toggles the unsuccessfulSell URL parameter based on currency state.
 * Ensures URL parameter state matches the current currency selection.
 *
 * @param currCurrency - The current currency value to check against default currency
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const conditionalToggleUnsuccessfulSellUrlParameter = (currCurrency: any): void => {
  const urlParams = new URLSearchParams(window.location.search);
  if ((urlParams?.get(UNSUCCESSFULSELL) === 'true') !== (currCurrency === DEFAULT_CURRENCY)) {
    toggleUnsuccessfulSellUrlParameter();
  }
};

/**
 * Updates session for special rates by clearing cluster and corporate codes.
 * Used when retrying search after special rate failures.
 */
export async function updateSessionForSpecialRates(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sessionData: any,
  SESSION_GET_CALL_URL_CLIENT: string,
  NEXT_PUBLIC_PREFIX: string
) {
  const requestId = useClientEnvVarsStore.getState().envVarsObject['REQUEST_ID'];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const removeBlock: any = [];
  const sessionObj = sessionData.data ? sessionData : sessionData.cacheData;
  const sessionToken = sessionObj?.data?.sessionToken || sessionObj?.key || getSessionIDFromCookie() || '';
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const AriesUpdateSessionPayloadObject: any = {};
    if (sessionObj?.data?.AriesSearch) {
      AriesUpdateSessionPayloadObject['AriesSearch.searchCriteria.availabilityRequestVO.clusterCode'] = 'none';
      AriesUpdateSessionPayloadObject['AriesSearch.searchCriteria.availabilityRequestVO.corporateCode'] = '';

      await axios.patch(
        SESSION_GET_CALL_URL_CLIENT,
        {
          createOrUpdate: AriesUpdateSessionPayloadObject,
          remove: removeBlock,
          sessionToken,
        },
        {
          headers: {
            Cookie: `sessionID=${sessionToken}`,
            'Access-Control-Allow-Origin': '*',
            'Content-Type': 'application/json',
            'x-request-id': requestId,
          },
        }
      );
    } else {
      return;
    }
  } catch (error) {
    const loggerApiURL = getLoggerApiURL(NEXT_PUBLIC_PREFIX, true);
    axios.post(loggerApiURL, {
      message: `Error while making session call with sessionId ${sessionData?.sessionToken}: ${JSON.stringify(error)}`,
    });
  }
}

/**
 * Padding type for fitBounds operations.
 * Can be a single number (uniform padding) or an object with individual side values.
 */
export type FitBoundsPadding =
  | number
  | {
      top?: number;
      right?: number;
      bottom?: number;
      left?: number;
    };

/**
 * Normalize padding to an object with all four sides.
 * Converts a single number to uniform padding on all sides.
 */
const normalizePadding = (padding: FitBoundsPadding): { top: number; right: number; bottom: number; left: number } => {
  if (typeof padding === 'number') {
    return { top: padding, right: padding, bottom: padding, left: padding };
  }
  return {
    top: padding.top ?? 0,
    right: padding.right ?? 0,
    bottom: padding.bottom ?? 0,
    left: padding.left ?? 0,
  };
};

/**
 * Calculate the optimal zoom level to fit bounds within a map container.
 * Uses the same mathematical calculation as smoothFitBounds.
 *
 * @param map - Google Maps instance
 * @param bounds - LatLngBounds to fit
 * @param padding - Padding in pixels around the bounds (uniform or per-side)
 * @returns Calculated zoom level (capped at 15)
 */
export const calculateFitBoundsZoom = (
  map: google.maps.Map,
  bounds: google.maps.LatLngBounds,
  padding: FitBoundsPadding
): number => {
  const p = normalizePadding(padding);
  const mapDiv = map.getDiv();
  const mapWidth = mapDiv.offsetWidth - p.left - p.right;
  const mapHeight = mapDiv.offsetHeight - p.top - p.bottom;

  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const latSpan = Math.abs(ne.lat() - sw.lat());
  const lngSpan = Math.abs(ne.lng() - sw.lng());

  let targetZoom = 14; // Default for single point

  if (latSpan > 0 || lngSpan > 0) {
    const WORLD_DIM = 256;
    const targetCenter = bounds.getCenter();

    const lngZoom = lngSpan > 0 ? Math.log2((mapWidth * 360) / (lngSpan * WORLD_DIM)) : 21;

    const latRad = (targetCenter.lat() * Math.PI) / 180;
    const mercatorFactor = Math.cos(latRad);
    const latZoom = latSpan > 0 ? Math.log2((mapHeight * 360 * mercatorFactor) / (latSpan * WORLD_DIM)) : 21;

    targetZoom = Math.min(Math.floor(Math.min(latZoom, lngZoom)), 21, 15);
  }

  return targetZoom;
};

/**
 * Smooth fitBounds - guarantees smooth animation using requestAnimationFrame.
 * Interpolates in screen coordinates for a straight-line path on screen.
 * Unlike native fitBounds which uses internal heuristics, this always animates smoothly.
 * Uses mathematical zoom calculation to avoid any visible map jumps.
 *
 * @param map - Google Maps instance
 * @param bounds - LatLngBounds to fit the map to
 * @param padding - Padding in pixels around the bounds (uniform or per-side)
 * @param duration - Animation duration in milliseconds (default: 800)
 * @param onComplete - Optional callback when animation completes
 */
export const smoothFitBounds = (
  map: google.maps.Map,
  bounds: google.maps.LatLngBounds,
  padding: FitBoundsPadding,
  duration: number = 800,
  onComplete?: () => void
) => {
  const projection = map.getProjection();
  const p = normalizePadding(padding);

  if (!projection) {
    map.fitBounds(bounds, { top: p.top, right: p.right, bottom: p.bottom, left: p.left });
    onComplete?.();
    return;
  }

  const startCenter = map.getCenter();
  const startZoom = map.getZoom() || 10;
  const targetCenter = bounds.getCenter();
  const targetZoom = calculateFitBoundsZoom(map, bounds, padding);

  if (!startCenter) {
    map.fitBounds(bounds, { top: p.top, right: p.right, bottom: p.bottom, left: p.left });
    onComplete?.();
    return;
  }

  // Get map container dimensions for screen-space calculations
  const mapDiv = map.getDiv();
  const mapWidth = mapDiv.offsetWidth;
  const mapHeight = mapDiv.offsetHeight;
  const screenCenterX = mapWidth / 2;
  const screenCenterY = mapHeight / 2;

  // Convert lat/lng to world coordinates (at zoom 0)
  const startWorldPoint = projection.fromLatLngToPoint(startCenter);
  const targetWorldPoint = projection.fromLatLngToPoint(targetCenter);

  if (!startWorldPoint || !targetWorldPoint) {
    map.fitBounds(bounds, { top: p.top, right: p.right, bottom: p.bottom, left: p.left });
    onComplete?.();
    return;
  }

  // Capture world coordinates for use in animate closure
  const targetWorldX = targetWorldPoint.x;
  const targetWorldY = targetWorldPoint.y;

  // Calculate the screen position of the target at the START zoom level
  // This tells us where the target appears on screen initially
  const scale = Math.pow(2, startZoom);
  const startTargetScreenX = screenCenterX + (targetWorldPoint.x - startWorldPoint.x) * scale;
  const startTargetScreenY = screenCenterY + (targetWorldPoint.y - startWorldPoint.y) * scale;

  const startTime = performance.now();

  function animate() {
    const elapsed = performance.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);

    // Use ease-out for smoother deceleration at the end
    const eased = 1 - Math.pow(1 - progress, 2);

    // Interpolate zoom
    const currentZoom = startZoom + (targetZoom - startZoom) * eased;
    const currentScale = Math.pow(2, currentZoom);

    // Interpolate the screen position of the target (straight line on screen)
    // Target moves from its initial screen position to screen center
    const currentTargetScreenX = startTargetScreenX + (screenCenterX - startTargetScreenX) * eased;
    const currentTargetScreenY = startTargetScreenY + (screenCenterY - startTargetScreenY) * eased;

    // Calculate what center lat/lng would place the target at currentTargetScreen position
    // Screen offset from center = (targetWorld - centerWorld) * scale
    // So: centerWorld = targetWorld - screenOffset / scale
    const screenOffsetX = currentTargetScreenX - screenCenterX;
    const screenOffsetY = currentTargetScreenY - screenCenterY;

    const currentCenterWorldX = targetWorldX - screenOffsetX / currentScale;
    const currentCenterWorldY = targetWorldY - screenOffsetY / currentScale;

    const currentCenterWorld = new google.maps.Point(currentCenterWorldX, currentCenterWorldY);
    const currentCenter = projection!.fromPointToLatLng(currentCenterWorld);

    if (currentCenter) {
      map.moveCamera({
        center: { lat: currentCenter.lat(), lng: currentCenter.lng() },
        zoom: currentZoom,
      });
    }

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (onComplete) {
      onComplete();
    }
  }

  requestAnimationFrame(animate);
};
